-- | Cached lookup data and effectful work-queue helpers.
-- |
-- | Durable queue selections live in `Steps.WorkQueue.State`; transient widget
-- | state lives in `Steps.WorkQueue.UIState`. This module owns server-derived
-- | lookup data and effectful cache population.
module Steps.WorkQueue.Cache
    ( WorkQueueCache
    , WorkQueueLoadResult
    , WorkQueuePreloadResult
    , initialWorkQueueCache
    , loadWorkQueue
    , lazyLoadWorkQueue
    , getForm
    , preloadStep3Resources
    , getFormType
    , getDoc
    , getDocByStudent
    , isLockedDocument
    , newDocument
    , getDocument
    , insertDocument
    ) where

import Common

import API.Account (AccountInfo, AccountType(..), queryAccountInfo)
import API.FormBase (FormBase, FormBasic, FormID)
import API.Document (CreateDocArgs, Document, createDocument, fetchDocumentQuery)
import API.FormType (FormType, formQueryRequest, getSelectedForm)
import API.RecentForms (RecentForm)
import API.Search (SearchData, SearchSemester, SearchTeacher, emptySearchData, searchDataQuery)
import API.Types (DocumentID)
import Control.Alt ((<|>))
import Control.Monad.Except (except)
import Control.Monad.Except.Trans (ExceptT, runExceptT)
import Data.Array as Arr
import Data.Map (Map)
import Data.Map as Map
import Data.Maybe (isJust, maybe)
import Data.Set as Set
import Data.Traversable (sequence)
import Data.Tuple (Tuple(..))
import Error (ErrorWrapper)
import GraphQL.Join (StringQuery, getDecode, getQuery, multiQuery_, runGraphQL_, runMultiQuery)
import GraphQL.Query (Query)
import Internal.SelectionMap (listSelected)
import Steps.WorkQueue.DocumentIndex (DocumentLookup, getDocById, getDocByLookup, insertDoc, listDocs)
import Steps.WorkQueue.State (WorkQueueState)
import Steps.WorkQueue.UIState (WorkQueueUiState)
import Widgets.BetterSelect (SelectOption, selectedItem, setSelected)
import Widgets.Search (loadSearchState)

type WorkQueueCache =
    { account :: AccountInfo
    , searchData :: SearchData
    , defTeacher :: Maybe (SelectOption SearchTeacher)
    , defSemester :: Maybe (SelectOption SearchSemester)
    , formCache :: Map FormID FormType
    , loaded :: Boolean
    }

type WorkQueueLoadResult =
    { state :: WorkQueueState
    , ui :: WorkQueueUiState
    , cache :: WorkQueueCache
    }

type WorkQueuePreloadResult =
    { state :: WorkQueueState
    , cache :: WorkQueueCache
    }

initialWorkQueueCache :: WorkQueueCache
initialWorkQueueCache =
    { account: { type: TeacherAccount, student: Nothing }
    , searchData: emptySearchData
    , defTeacher: Nothing
    , defSemester: Nothing
    , formCache: Map.empty
    , loaded: false
    }

loadWorkQueue
    :: WorkQueueState
    -> WorkQueueUiState
    -> WorkQueueCache
    -> Aff (Either ErrorWrapper WorkQueueLoadResult)
loadWorkQueue state ui cache = runExceptT do
    { account, searchData } <- multiQuery_
        { account: queryAccountInfo
        , searchData: searchDataQuery
        }
    let defs = searchData.defaults
    let defTeacher = (\v -> { label: v.name, value: v }) <$> defs.teacher
    let defSemester = (\v -> { label: v.name, value: v }) <$> defs.semester
    let ui' = ui
            { teacher = setSelected ui.teacher defTeacher
            , semester = setSelected ui.semester defSemester
            , search = loadSearchState searchData
            }
    pure
        { state: state
            { teacher = _.value <$> selectedItem ui'.teacher
            , semester = _.value <$> selectedItem ui'.semester
            }
        , ui: ui'
        , cache: cache
            { account = account
            , defTeacher = defTeacher
            , defSemester = defSemester
            , searchData = searchData
            }
        }

lazyLoadWorkQueue
    :: WorkQueueState
    -> WorkQueueUiState
    -> WorkQueueCache
    -> Aff (Either ErrorWrapper WorkQueueLoadResult)
lazyLoadWorkQueue state ui cache =
    if cache.loaded
        then pure $ Right { state, ui, cache }
        else loadWorkQueue state ui cache

getForm :: WorkQueueState -> FormID -> Maybe FormBasic
getForm state id = getSelectedForm id state.forms

-- | Preloads the Step 3 prerequisites before the real editor state is built.
-- |
-- | This seeds any route-provided resources, loads missing form layouts for the
-- | current queue, and fetches the active Step 3 document when `currentDoc`
-- | points at an edit queue entry that has not been cached yet.
preloadStep3Resources
    :: WorkQueueState
    -> WorkQueueCache
    -> Maybe FormType
    -> Maybe Document
    -> ExceptT ErrorWrapper Aff WorkQueuePreloadResult
preloadStep3Resources state cache maybeFtype maybeDoc = do
    let ftypesSel = Set.fromFoldable $ map _.id $ listSelected state.forms
    let ftypesEdit = Set.fromFoldable $ map _.form_type.id state.editDocs
    let seeded = seedPreload state cache maybeFtype maybeDoc
    let ftypeIds = Arr.fromFoldable $ Set.union ftypesSel ftypesEdit
    let missingFtypeIds = Arr.filter (\id -> not $ Map.member id seeded.cache.formCache) ftypeIds
    let currentDocQuery = currentDocumentQuery seeded.state maybeDoc
    let preloadQueries =
            map (Step3FormTypeQuery <<< formQueryRequest) missingFtypeIds
                <> maybe [] (\query -> [ query ]) currentDocQuery

    preload <- runStep3PreloadQueries preloadQueries
    let formCache = foldr (\f m -> Map.insert f.id f m) seeded.cache.formCache preload.formTypes
    pure
        { state: foldl (flip insertDocument) seeded.state preload.documents
        , cache: seeded.cache { formCache = formCache }
        }
  where
    seedPreload
        :: WorkQueueState
        -> WorkQueueCache
        -> Maybe FormType
        -> Maybe Document
        -> WorkQueuePreloadResult
    seedPreload state' cache' maybeFtype' maybeDoc' =
        let withFormType = case maybeFtype' of
                Nothing -> cache'
                Just ftype -> cache' { formCache = Map.insert ftype.id ftype cache'.formCache }
        in case maybeDoc' of
            Nothing -> { state: state', cache: withFormType }
            Just doc -> { state: insertDocument doc state', cache: withFormType }

    currentDocumentQuery :: WorkQueueState -> Maybe Document -> Maybe Step3PreloadQuery
    currentDocumentQuery state' maybeDoc' = case maybeDoc', state'.currentDoc of
        Just _, _ ->
            Nothing

        Nothing, Just lookup ->
            if isJust (getDocByStudent state' lookup)
                then Nothing
                else map (\recentForm -> Step3DocumentQuery $ fetchDocumentQuery recentForm.form_type recentForm.id) $
                    Arr.find (matchesRecentFormLookup lookup) state'.editDocs

        Nothing, Nothing ->
            Nothing

data Step3PreloadQuery
    = Step3FormTypeQuery (StringQuery FormType)
    | Step3DocumentQuery (Query Document)

data Step3PreloadResult
    = Step3FormTypeResult FormType
    | Step3DocumentResult Document

runStep3PreloadQueries
    :: Array Step3PreloadQuery
    -> ExceptT ErrorWrapper Aff { formTypes :: Array FormType, documents :: Array Document }
runStep3PreloadQueries queries
    | Arr.null queries = pure { formTypes: [], documents: [] }
    | otherwise = do
        responses <- runMultiQuery $ map step3PreloadRequest queries
        decoded <- except $ sequence $ Arr.zipWith decodeStep3PreloadQuery queries responses
        pure $ foldl collectPreloadedStep3Resources { formTypes: [], documents: [] } decoded

step3PreloadRequest :: Step3PreloadQuery -> { query :: String, variables :: Json }
step3PreloadRequest = case _ of
    Step3FormTypeQuery query -> getQuery query
    Step3DocumentQuery query -> getQuery query

decodeStep3PreloadQuery :: Step3PreloadQuery -> Json -> Either ErrorWrapper Step3PreloadResult
decodeStep3PreloadQuery query json = case query of
    Step3FormTypeQuery request ->
        Step3FormTypeResult <$> getDecode request json
    Step3DocumentQuery request ->
        Step3DocumentResult <$> getDecode request json

getFormType :: WorkQueueCache -> FormID -> Maybe FormType
getFormType cache formId = Map.lookup formId cache.formCache

getDoc :: WorkQueueState -> DocumentID -> Maybe Document
getDoc state id = getDocById id state.documents

getDocByStudent :: WorkQueueState -> DocumentLookup -> Maybe Document
getDocByStudent state dl =
    case dl.documentId of
        Just documentId ->
            getDocById documentId state.documents
        Nothing ->
            getDocByLookup dl state.documents <|> Arr.find (matchesDocumentLookup dl) loadedDocs
  where
    loadedDocs = Arr.fromFoldable $ listDocs state.documents

isLockedDocument :: WorkQueueState -> DocumentLookup -> Boolean
isLockedDocument state lookup =
    maybe recentFormLocked (isJust <<< _.locked_at) $ getDocByStudent state lookup
  where
    recentFormLocked = maybe false (isJust <<< _.locked_at) $
        Arr.find (matchesRecentFormLookup lookup) state.editDocs

matchesDocumentLookup :: DocumentLookup -> Document -> Boolean
matchesDocumentLookup lookup doc = case lookup.documentId of
    Just documentId -> doc.id == documentId
    Nothing ->
        doc.student.id == lookup.studentId
            && doc.formId == lookup.formId

matchesRecentFormLookup :: DocumentLookup -> RecentForm -> Boolean
matchesRecentFormLookup lookup recentForm = case lookup.documentId of
    Just documentId -> recentForm.id == documentId
    Nothing ->
        recentForm.student.id == lookup.studentId
            && recentForm.form_type.id == lookup.formId

-- | Creates a new document and adds it to the work queue document map.
-- | Returns the new queue state and the created document.
newDocument :: forall r. WorkQueueState -> FormBase r -> CreateDocArgs
    -> ExceptT ErrorWrapper Aff (Tuple WorkQueueState Document)
newDocument state ftype args = do
    doc <- runGraphQL_ $ createDocument ftype args
    let newState = insertDocument doc state
    pure $ Tuple newState doc

-- | Fetches a document by ID from the backend and stores it in the document map.
getDocument :: WorkQueueState -> FormBasic -> DocumentID -> ExceptT ErrorWrapper Aff (Tuple WorkQueueState Document)
getDocument state ftype docId = do
    doc <- runGraphQL_ $ fetchDocumentQuery ftype docId
    let newState = insertDocument doc state
    pure $ Tuple newState doc

-- | Adds or updates a document in the work queue.
-- |
-- | If a document with the same ID exists, it will be replaced, otherwise a
-- | new one will be added.
insertDocument :: Document -> WorkQueueState -> WorkQueueState
insertDocument doc state = state { documents = insertDoc doc state.documents }

collectPreloadedStep3Resources
    :: { formTypes :: Array FormType, documents :: Array Document }
    -> Step3PreloadResult
    -> { formTypes :: Array FormType, documents :: Array Document }
collectPreloadedStep3Resources acc = case _ of
    Step3FormTypeResult ftype ->
        acc { formTypes = Arr.snoc acc.formTypes ftype }
    Step3DocumentResult doc ->
        acc { documents = Arr.snoc acc.documents doc }
